import { useState, useEffect, useCallback, useMemo } from 'react';
import { SupplierBrandManagement } from "@/polymet/components/supplier-brand-management";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Save, Loader2, Download, PlusCircleIcon } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SupplierService } from "@/lib/supplier-service";
import { supabase, getCurrentUser } from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { getProductDetailUrl } from "@/lib/navigation-helpers";

// Define the form schema for supplier profile
const supplierProfileSchema = z.object({
  // Basic information
  businessName: z.string().min(2, { message: "Business name must be at least 2 characters" }),
  description: z.string().optional(),
  website: z.string().url({ message: "Please enter a valid URL" }).optional().or(z.literal('')),
  address: z.string().optional(),
  logoUrl: z.string().optional(),
  
  // Business details
  establishmentYear: z.coerce.number().min(1900).max(new Date().getFullYear()).optional(),
  natureOfBusiness: z.string().optional(),
  legalStatus: z.string().optional(),
  
  // Registration details
  gstNumber: z.string().optional(),
  gstRegistrationDate: z.string().optional(),
  panNumber: z.string().optional(),
  importExportCode: z.string().optional(),
  
  // Business metrics
  annualTurnover: z.string().optional(),
  employeeCount: z.string().optional(),
  
  // Categories
  productCategories: z.array(z.string()).optional(),
  
  // Certifications
  certifications: z.array(
    z.object({
      name: z.string(),
      verified: z.boolean().default(false)
    })
  ).default([]),
  
  // Company information
  about: z.string().optional(),
  mission: z.string().optional(),
  vision: z.string().optional(),
  mainMarkets: z.array(z.string()).optional(),
  
  // Verification
  trustSealVerified: z.boolean().optional(),
  machinesearchCertification: z.boolean().optional(),
});

// Define the form values type using Zod inference
type SupplierProfileFormValues = z.infer<typeof supplierProfileSchema>;

// Define the certification type for reuse
interface Certification {
  name: string;
  verified: boolean;
}

// Define the supplier type
interface Supplier {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  company: string;
  position: string;
  location: string;
  notificationCount: number;
  verificationStatus: string;
  businessName: string;
  logo: string;
  description: string;
  website: string;
  gstNumber: string;
  panNumber: string;
  address: string;
}

// Define the document type
interface Document {
  id: string;
  document_type: string;
  document_url: string;
  created_at: string;
  verified: boolean;
}

// Define the stats type
interface Stats {
  products: number;
  inquiries: number;
  orders: number;
  revenue: number;
}

// Define business type options
const businessTypes = [
  "Manufacturer",
  "Wholesaler",
  "Retailer",
  "Service Provider",
  "Exporter",
  "Importer"
];

// Define legal status options
const legalStatusOptions = [
  "Sole Proprietorship",
  "Partnership",
  "Limited Company",
  "Corporation",
  "LLC",
  "LLP"
];

// Define turnover ranges
const turnoverRanges = [
  "Under 5 Lakhs",
  "5 - 25 Lakhs",
  "25 Lakhs - 1 Cr",
  "1 - 5 Cr",
  "5 - 25 Cr",
  "25 - 100 Cr",
  "100 Cr & Above"
];

// Define employee count ranges
const employeeCountRanges = [
  "1-10 People",
  "11-25 People",
  "26-50 People",
  "51-100 People",
  "101-500 People",
  "501-1000 People",
  "1000+ People"
];

const SupplierDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State for active tab
  const [activeTab, setActiveTab] = useState<string>("overview");
  
  // State for success message
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  
  // State for supplier data
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [profileUpdated, setProfileUpdated] = useState<boolean>(false);

  // Add products state to store loaded products
  const [products, setProducts] = useState<any[]>([]);

  const [documentType, setDocumentType] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);

  const [stats, setStats] = useState<Stats>({ products: 0, inquiries: 0, orders: 0, revenue: 0 });
  
  // Initialize form for profile editing
  const profileForm = useForm<SupplierProfileFormValues>({
    resolver: zodResolver(supplierProfileSchema) as any, // Type assertion needed due to complex schema
    defaultValues: {
      // Basic information
      businessName: '',
      description: '',
      website: '',
      address: '',
      logoUrl: '',
      
      // Business details
      establishmentYear: undefined,
      natureOfBusiness: '',
      legalStatus: '',
      
      // Registration details
      gstNumber: '',
      gstRegistrationDate: '',
      panNumber: '',
      importExportCode: '',
      
      // Business metrics
      annualTurnover: '',
      employeeCount: '',
      
      // Categories
      productCategories: [],
      
      // Certifications
      certifications: [] as Certification[],
      
      // Company information
      about: '',
      mission: '',
      vision: '',
      mainMarkets: [],
      
      // Verification
      trustSealVerified: false,
      machinesearchCertification: false,
    },
  });

  // Create instance of supplier service
  const supplierService = useMemo(() => new SupplierService(), []);

  // Load supplier dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get current user
      const user = await getCurrentUser();
      if (!user) {
        console.error('No authenticated user found');
        setLoading(false);
        return;
      }

      // Variable to store supplier data
      let supplierData = null;

      try {
        // Get complete supplier information
        const completeInfo = await supplierService.getCompleteSupplierInfo(user.id);
        
        if (!completeInfo) {
          console.error('Failed to load supplier information');
          setLoading(false);
          return;
        }
        
        // Extract profile and supplier profile data
        const profile = completeInfo;
        const supplierProfileDetails = completeInfo.supplier_profile;
        
        // Check if user is a supplier
        if (profile.user_type !== 'supplier' && profile.user_type !== 'both') {
          console.error('User is not a supplier or both');
          setLoading(false);
          return;
        }
        
        // Format supplier data with strict null checks
        supplierData = {
          id: profile.id || '',
          name: profile.full_name || 'Supplier',
          email: profile.email || '',
          phone: profile.phone || '',
          avatar: profile.avatar_url || 'https://github.com/polymet-ai.png',
          company: profile.company_name || '',
          position: 'Supplier',
          location: `${profile.city || ''}, ${profile.state || ''}`,
          notificationCount: 0,
          verificationStatus: profile.is_verified ? 'verified' : 'pending',
          businessName: (supplierProfileDetails && supplierProfileDetails.business_name) ? supplierProfileDetails.business_name : (profile.company_name || ''),
          logo: (supplierProfileDetails && supplierProfileDetails.logo_url) ? supplierProfileDetails.logo_url : (profile.avatar_url || ''),
          description: (supplierProfileDetails && supplierProfileDetails.description) ? supplierProfileDetails.description : '',
          website: (supplierProfileDetails && supplierProfileDetails.website) ? supplierProfileDetails.website : '',
          gstNumber: (supplierProfileDetails && supplierProfileDetails.gst_number) ? supplierProfileDetails.gst_number : '',
          panNumber: (supplierProfileDetails && supplierProfileDetails.pan_number) ? supplierProfileDetails.pan_number : '',
          address: (supplierProfileDetails && supplierProfileDetails.address) ? supplierProfileDetails.address : '',
        } as Supplier;

        setSupplier(supplierData);
        
        // Populate the profile form with current data
        profileForm.reset({
          businessName: supplierData.businessName,
          description: supplierData.description,
          website: supplierData.website,
          gstNumber: supplierData.gstNumber,
          panNumber: supplierData.panNumber,
          address: supplierData.address,
          logoUrl: supplierData.logo,
          establishmentYear: supplierProfileDetails?.establishment_year as number | undefined,
          natureOfBusiness: supplierProfileDetails?.nature_of_business,
          legalStatus: supplierProfileDetails?.legal_status,
          gstRegistrationDate: supplierProfileDetails?.gst_registration_date,
          importExportCode: supplierProfileDetails?.import_export_code,
          annualTurnover: supplierProfileDetails?.annual_turnover,
          employeeCount: supplierProfileDetails?.employee_count,
          productCategories: (supplierProfileDetails?.product_categories || []) as string[],
          certifications: (supplierProfileDetails?.certifications || []) as Certification[],
          about: supplierProfileDetails?.about || '',
          mission: supplierProfileDetails?.mission || '',
          vision: supplierProfileDetails?.vision || '',
          mainMarkets: (supplierProfileDetails?.main_markets || []) as string[],
          trustSealVerified: supplierProfileDetails?.trust_seal_verified || false,
          machinesearchCertification: supplierProfileDetails?.machinesearch_certification || false
        });

        // Load products
        const productsData = await supplierService.getSupplierProducts(user.id);
        
        setProducts(productsData || []);

        // Load inquiries
        const inquiriesData = await supplierService.getSupplierInquiries(user.id);
        

        // Load orders
        const { data: ordersData } = await supabase
          .from('orders')
          .select('*')
          .eq('supplier_id', user.id)
          .order('created_at', { ascending: false });
        
        
        // Load supplier documents
        const { data: documentsData } = await supabase
          .from('supplier_documents')
          .select('*')
          .eq('supplier_id', user.id)
          .order('created_at', { ascending: false });
        setDocuments(documentsData || []);

        // Calculate stats
        setStats({
          products: productsData?.length || 0,
          inquiries: inquiriesData?.length || 0,
          orders: ordersData?.length || 0,
          revenue: ordersData?.reduce((sum: number, order: any) => sum + (order.total_amount || 0), 0) || 0
        });

        setLoading(false);
      } catch (error) {
        console.error('Error loading supplier data:', error);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error getting current user:', error);
      setLoading(false);
    }
  }, [supplierService, profileForm]);

  // Handle add product navigation
  const handleAddProduct = useCallback(() => {
    navigate('/supplier/products/add');
  }, [navigate]);
  
  // Handle document file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };
  
  // Handle document upload
  const handleDocumentUpload = useCallback(async () => {
    try {
      if (!selectedFile || !documentType) {
        alert('Please select a document and document type');
        return;
      }
      
      setIsUploading(true);
      
      // Get current user
      const user = await getCurrentUser();
      if (!user) {
        console.error('No authenticated user found');
        setIsUploading(false);
        return;
      }
      
      // Upload file to storage
      const fileName = `${user.id}/${Date.now()}-${selectedFile.name}`;
      const { error: uploadError } = await supabase.storage
        .from('supplier-documents')
        .upload(fileName, selectedFile);
        
      if (uploadError) throw uploadError;
      
      // Get public URL
      const { data: urlData } = supabase.storage
        .from('supplier-documents')
        .getPublicUrl(fileName);
      
      // Add document record to database
      const { error: docError } = await supabase
        .from('supplier_documents')
        .insert({
          supplier_id: user.id,
          document_type: documentType,
          document_url: urlData.publicUrl,
          verified: false
        });
        
      if (docError) throw docError;
      
      alert('Document uploaded successfully');
      setSelectedFile(null);
      setDocumentType('');
      
      // Reload documents
      await loadDashboardData();
      
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, documentType, loadDashboardData]);
  
  // Handle document deletion
  const handleDeleteDocument = useCallback(async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) {
      return;
    }
    
    try {
      const { error } = await supabase
        .from('supplier_documents')
        .delete()
        .eq('id', documentId);
        
      if (error) throw error;
      
      alert('Document deleted successfully');
      
      // Update documents list
      setDocuments(documents.filter(doc => doc.id !== documentId));
      
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document. Please try again.');
    }
  }, [documents]);

  // Handle product deletion
  const handleDeleteProduct = useCallback(async (productId: string) => {
    try {
      setLoading(true);
      
      // First delete any product images
      const { error: imageDeleteError } = await supabase
        .from('product_images')
        .delete()
        .eq('product_id', productId);
      
      if (imageDeleteError) {
        console.warn('Error deleting product images:', imageDeleteError);
        // Continue with product deletion even if image deletion fails
      }
      
      // Delete the product
      const { error: productDeleteError } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);
      
      if (productDeleteError) throw productDeleteError;
      
      // Update local state
      setProducts(products.filter(product => product.id !== productId));
      
      // Update stats
      setStats(prevStats => ({
        ...prevStats,
        products: prevStats.products - 1
      }));
      
      alert('Product deleted successfully');
      
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [products]);

  // Function to update supplier profile
  const updateSupplierProfile = useCallback(async (data: SupplierProfileFormValues) => {
    setIsSaving(true);
    try {
      // Get current user
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        console.error('No authenticated user found');
        setIsSaving(false);
        return;
      }
      
      // Update the supplier profile with all new fields
      const { error } = await supabase
        .from('supplier_profiles')
        .update({
          // Basic information
          business_name: data.businessName,
          description: data.description,
          website: data.website,
          address: data.address,
          logo: data.logoUrl,
          
          // Business details
          establishment_year: data.establishmentYear,
          nature_of_business: data.natureOfBusiness,
          legal_status: data.legalStatus,
          
          // Registration details
          gst_number: data.gstNumber,
          gst_registration_date: data.gstRegistrationDate,
          pan_number: data.panNumber,
          import_export_code: data.importExportCode,
          
          // Business metrics
          annual_turnover: data.annualTurnover,
          employee_count: data.employeeCount,
          
          // Company information
          about: data.about,
          mission: data.mission,
          vision: data.vision,
          main_markets: data.mainMarkets,
          
          // Verification
          trust_seal_verified: data.trustSealVerified,
          machinesearch_certification: data.machinesearchCertification,
          
          // Other fields
          product_categories: data.productCategories,
          certifications: data.certifications,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentUser.id);
      
      if (error) {
        throw error;
      }
      
      // Show success message
      alert('Profile updated successfully!');
      setProfileUpdated(true);
      
      // Reload dashboard data
      await loadDashboardData();
      
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [loadDashboardData]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Handle URL query parameters for tab switching and success messages
  useEffect(() => {
    const tab = searchParams.get('tab');
    const success = searchParams.get('success');
    
    if (tab) {
      setActiveTab(tab);
    }
    
    if (success === 'true') {
      setShowSuccessMessage(true);
      // Clear the success message after 5 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
        // Remove the success query parameter from URL
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('success');
        navigate(`/supplier/dashboard${tab ? `?tab=${tab}` : ''}`, { replace: true });
      }, 5000);
    }
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Success Message */}
      {showSuccessMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>Product added successfully! You can view it in the products list below.</span>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16 border-2 border-primary">
            <AvatarImage src={supplier?.avatar} />
            <AvatarFallback>{supplier?.name?.charAt(0) || 'S'}</AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl font-bold">{supplier?.name || 'Supplier Dashboard'}</h1>
            <p className="text-muted-foreground">{supplier?.company || 'Industrial Equipment Supplier'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleAddProduct}
            className="bg-primary hover:bg-primary/90"
          >
            Add Product
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setActiveTab("profile")}
          >
            Edit Profile
          </Button>
        </div>
      </div>

      {/* Custom Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-border">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("overview")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "overview"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300"
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab("profile")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "profile"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300"
              }`}
            >
              Profile
            </button>
            <button
              onClick={() => setActiveTab("brands")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "brands"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300"
              }`}
            >
              Brands
            </button>
            <button
              onClick={() => setActiveTab("products")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "products"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300"
              }`}
            >
              Products
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content - All tabs are always rendered but only one is visible */}
      <div className="space-y-6">
        {/* Overview Tab */}
        <div style={{ display: activeTab === "overview" ? "block" : "none" }}>
          <div className="space-y-6">
            {/* Dashboard Overview */}
            <Card>
              <CardHeader>
                <CardTitle>Dashboard Overview</CardTitle>
                <CardDescription>View your supplier performance at a glance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold">{stats.products}</div>
                      <p className="text-muted-foreground">Total Products</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold">{stats.inquiries}</div>
                      <p className="text-muted-foreground">New Inquiries</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold">{stats.orders}</div>
                      <p className="text-muted-foreground">Active Orders</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold">₹{stats.revenue.toLocaleString()}</div>
                      <p className="text-muted-foreground">Total Revenue</p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Profile Tab */}
        <div style={{ display: activeTab === "profile" ? "block" : "none" }}>
          <Card>
            <CardHeader>
              <CardTitle>Supplier Profile</CardTitle>
              <CardDescription>Update your business information and profile details</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Profile form content will be implemented here...</p>
            </CardContent>
          </Card>
        </div>

        {/* Brands Tab */}
        <div style={{ display: activeTab === "brands" ? "block" : "none" }}>
          <SupplierBrandManagement supplierId={supplier?.id || ""} />
        </div>

        {/* Products Tab */}
        <div style={{ display: activeTab === "products" ? "block" : "none" }}>
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>My Products</CardTitle>
                  <CardDescription>
                    Manage your product listings, track performance, and update inventory
                  </CardDescription>
                </div>
                <Button onClick={handleAddProduct}>
                  <PlusCircleIcon className="h-4 w-4 mr-2" />
                  Add New Product
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {products.length === 0 ? (
                <div className="text-center py-12">
                  <div className="mx-auto max-w-sm">
                    <div className="bg-muted rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                      <PlusCircleIcon className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">No products yet</h3>
                    <p className="text-muted-foreground mb-6">
                      Start by adding your first product to showcase your inventory to potential buyers.
                    </p>
                    <Button onClick={handleAddProduct} size="lg">
                      <PlusCircleIcon className="h-4 w-4 mr-2" />
                      Add Your First Product
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {products.length} product{products.length === 1 ? '' : 's'} listed
                    </p>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-2" />
                        Export
                      </Button>
                      <Button variant="outline" size="sm">
                        <Loader2 className="h-4 w-4 mr-2" />
                        Bulk Actions
                      </Button>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="border-b bg-muted/50">
                          <tr>
                            <th className="text-left p-4 font-medium">Product</th>
                            <th className="text-left p-4 font-medium">Category</th>
                            <th className="text-left p-4 font-medium">Condition</th>
                            <th className="text-left p-4 font-medium">Price</th>
                            <th className="text-left p-4 font-medium">Stock</th>
                            <th className="text-left p-4 font-medium">Status</th>
                            <th className="text-left p-4 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map((product, index) => (
                            <tr key={product.id} className="border-b hover:bg-muted/25">
                              <td className="p-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                                    {product.main_image || (product.images && product.images.length > 0) ? (
                                      <img 
                                        src={product.main_image || product.images[0]} 
                                        alt={product.name}
                                        className="w-full h-full object-cover rounded-lg"
                                        onError={(e) => {
                                          // Hide image on error and show placeholder
                                          const target = e.target as HTMLImageElement;
                                          target.style.display = 'none';
                                        }}
                                      />
                                    ) : (
                                      <div className="w-8 h-8 bg-gray-300 rounded flex items-center justify-center">
                                        <span className="text-gray-500 text-xs">No Image</span>
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-medium line-clamp-1">{product.name}</p>
                                    <p className="text-sm text-muted-foreground line-clamp-1">
                                      {product.manufacturer || 'N/A'} {product.model || ''}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="p-4">
                                <span className="text-sm">{product.category_name || 'Uncategorized'}</span>
                              </td>
                              <td className="p-4">
                                <Badge variant={product.condition === 'New' ? 'default' : 'secondary'}>
                                  {product.condition || 'N/A'}
                                </Badge>
                              </td>
                              <td className="p-4">
                                <span className="font-medium">
                                  ₹{product.price?.toLocaleString() || '0'}
                                </span>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${
                                    product.in_stock ? 'bg-green-500' : 'bg-red-500'
                                  }`}></div>
                                  <span className="text-sm">
                                    {product.in_stock ? 'In Stock' : 'Out of Stock'}
                                  </span>
                                </div>
                              </td>
                              <td className="p-4">
                                <Badge variant={product.featured ? 'default' : 'outline'}>
                                  {product.featured ? 'Featured' : 'Standard'}
                                </Badge>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center gap-2">
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => window.open(getProductDetailUrl(product.slug), '_blank')}
                                  >
                                    View
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => navigate(`/supplier/products/edit/${product.id}`)}
                                  >
                                    Edit
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => {
                                      if (confirm('Are you sure you want to delete this product?')) {
                                        handleDeleteProduct(product.id);
                                      }
                                    }}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SupplierDashboard;
